// In this file, all Page components from 'src/pages` are auto-imported. Nested
// directories are supported, and should be uppercase. Each subdirectory will be
// prepended onto the component name.
//
// Examples:
//
// 'src/pages/HomePage/HomePage.js'         -> HomePage

import { useEffect } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import { Router, Route, Set, PrivateSet } from '@redwoodjs/router'

import { useAuth } from './auth'
import ApplicationLoadingScreen from './components/Feedback/ApplicationLoadingScreen/ApplicationLoadingScreen'
import SkeletonLoader from './components/Feedback/SkeletonLoader/SkeletonLoader'
import AdminLayout from './layouts/AdminLayout/AdminLayout'
import DashboardLayout from './layouts/DashboardLayout/DashboardLayout'
import { entryDoorFromPath, recordEntryDoor } from './lib/rumEntryDoor'
import ActivitiesPage from './pages/ActivitiesPage/ActivitiesPage'
import { MainAccidentsScreen } from './pages/DriverApplicationPage/screens/AccidentsScreen'
import { CurrentAddressScreen } from './pages/DriverApplicationPage/screens/AddressScreen'
import CompanyQuestionsScreen from './pages/DriverApplicationPage/screens/CompanyQuestionsScreen'
import ConsentScreen from './pages/DriverApplicationPage/screens/ConsentScreen'
import { MainEmploymentScreen } from './pages/DriverApplicationPage/screens/EmploymentScreen'
import { EquipmentScreen } from './pages/DriverApplicationPage/screens/EquipmentScreen'
import { LicenseScreen } from './pages/DriverApplicationPage/screens/LicenseScreen'
import { PersonalInformationScreen } from './pages/DriverApplicationPage/screens/PersonalInformationScreen'
import ReviewScreen from './pages/DriverApplicationPage/screens/ReviewScreen'
import SignScreen from './pages/DriverApplicationPage/screens/SignScreen'
import SubmitScreen from './pages/DriverApplicationPage/screens/SubmitScreen'
import { FormProvider } from './pages/DriverApplicationPage/utils/FormContext'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage/PrivacyPolicyPage'
import PublicShortFormPage from './pages/PublicShortFormPage/PublicShortFormPage'
import ShortFormPage from './pages/ShortFormPage/ShortFormPage'
import TermsOfServicePage from './pages/TermsOfServicePage/TermsOfServicePage'

const getDriverAppHost = (): string | null => {
  const domain = process.env.DRIVER_APP_DOMAIN
  if (!domain) return null
  try {
    return new URL(domain).hostname
  } catch {
    return null
  }
}

const Routes = () => {
  const { currentUser, loading } = useAuth()

  useEffect(() => {
    if (typeof window === 'undefined') return
    const door = entryDoorFromPath(window.location.pathname)
    if (door) recordEntryDoor(door)
  }, [])

  useEffect(() => {
    if (!loading && currentUser && currentUser.userId && (currentUser.roles.includes('RECRUITER') || currentUser.roles.includes('ADMIN') || currentUser.roles.includes('OPS'))) {
      datadogRum.setUser({
        id: currentUser.userId,
        email: currentUser.email,
        phone: currentUser.phone,
        roles: currentUser.roles,
        company: currentUser.companyId,
        companyName: currentUser.companyName,
      })
    }
  }, [currentUser, loading])

  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const driverAppHost = getDriverAppHost()
  const isDriverApp = !!driverAppHost && host === driverAppHost

  return (
    <Router useAuth={useAuth}>
      <Route path="/forbidden" page={ForbiddenPage} name="forbidden" />
      <Route path="/logout" page={LogoutPage} name="logout" />
      <Route path="/privacy/{companyId:String}" page={PrivacyPolicyPage} name="privacyPolicy" />
      <Route path="/terms/{companyId:String}" page={TermsOfServicePage} name="termsOfService" />

      <Route path="/driver-login" page={DriverLoginPage} name="driverLogin" />
      <Route path="/short-form/{trackingLink}" page={ShortFormPage} name="shortForm" />
      <Route path="/public-short-form/{companyId:String}" page={PublicShortFormPage} name="publicShortForm" />
      <Route path="/c/{companySlug:String}" page={CompanySlugPage} name="companySlug" />
      <Route path="/c/{companySlug:String}/{step:String}" page={CompanySlugPage} name="companySlugStep" />
      <Route path="/c" page={CompanySlugPage} name="companySlugCallback" />
      <PrivateSet unauthenticated="driverLogin" roles={['DRIVER', 'ADMIN']} wrap={[FormProvider]} whileLoadingAuth={ApplicationLoadingScreen} whileLoadingPage={ApplicationLoadingScreen}>
        <Route path="/driver-application/address-information" page={CurrentAddressScreen} name="addressInformation" />
        <Route path="/driver-application/license-information" page={LicenseScreen} name="licenseInformation" />
        <Route path="/driver-application/equipment-information" page={EquipmentScreen} name="equipmentInformation" />
        <Route path="/driver-application/accident-history" page={MainAccidentsScreen} name="accidentInformation" />
        <Route path="/driver-application/employment-history" page={MainEmploymentScreen} name="employmentInformation" />
        <Route path="/driver-application/company-questions" page={CompanyQuestionsScreen} name="companyQuestions" />
        <Route path="/driver-application/consents" page={ConsentScreen} name="consentsInformation" />
        <Route path="/driver-application/review" page={ReviewScreen} name="reviewInformation" />
        <Route path="/driver-application/sign" page={SignScreen} name="signInformation" />
        <Route path="/driver-application/submit" page={SubmitScreen} name="submitInformation" />
        <Route path="/driver-application" page={PersonalInformationScreen} name="personalInformation" />
      </PrivateSet>

      {!isDriverApp && (
        <Set>
          <Route path="/login" page={LoginPage} name="login" />
          <Route path="/oauth-nylas-callback" page={OAuthNylasCallbackPage} name="nylasOauthCallback" />
          <Route path="/voe-login" page={VoELoginPage} name="voeLogin" />
          <Route path="/voe-response-v2/{voeId:String}" page={VoEResponseV2Page} name="voeResponseV2" />
          <Route path="/voe-opt-out/{voeId:String}" page={VoEOptOutPage} name="voeOptOut" />
          <Route path="/external-voe-request/{companyId:string}" page={ExternalVoeRequestPage} name="externalVoeRequest" />
          <PrivateSet unauthenticated="voeLogin" roles={['EXTERNAL_COMPANY', 'ADMIN']}>
            <Route path="/voe-response" page={VoEResponsePage} name="voeResponse" />
          </PrivateSet>
          <PrivateSet wrap={DashboardLayout} unauthenticated="login" roles={['RECRUITER', 'ADMIN', 'OPS']} whileLoadingAuth={SkeletonLoader} whileLoadingPage={SkeletonLoader}>
            <Route path="/" page={DashboardPage} name="dashboard" />
            <Route path="/agenda-dashboard" page={AgendaDashboardPage} name="agendaDashboard" />
            <Route path="/applicant-not-found" page={ApplicantNotFoundPage} name="applicantNotFound" />
            <Route path="/applicants/{id:String}" page={ApplicantDetailsPage} name="applicantDetails" />
            <Route path="/applicants/{id:String}/{tab:String}" page={ApplicantDetailsPage} name="applicantDetails" />
            <Route path="/applicants" page={ApplicantsPage} name="applicants" />
            <Route path="/jobs" page={JobsPage} name="jobs" />
            <Route path="/settings" page={SettingsPage} name="settings" />
            <Route path="/voe-requests/{id:String}" page={VoERequestDetailsPage} name="voeRequestDetails" />
            <Route path="/voe-requests" page={VoERequestsPage} name="voeRequests" />
            <Route path="/applicants/{id}/{tab}/{voeId}/review" page={ApplicantDetailsPage} name="voeResponseReview" />
            {/* Legacy alias for old links; keep query string handling the same */}
            <Route path="/applicants/{id}/{tab}/{voeId}/call" page={ApplicantDetailsPage} name="voeResponseCallLegacy" />
            <Route path="/applicants/{id}/{tab}/{voeId}/detail" page={VoEResponseDetailPage} name="voeResponseDetails" />
            <Route path="/analytics" page={AnalyticsPage} name="analytics" />
            <Route path="/activity-analytics/calls" page={CallAnalyticsPage} name="activityAnalyticsCalls" />
            <Route path="/activity-analytics/sms" page={SMSAnalyticsPage} name="activityAnalyticsSms" />
            <Route path="/activity-analytics/email" page={EmailAnalyticsPage} name="activityAnalyticsEmail" />
            <Route path="/activity-analytics" redirect="/activity-analytics/calls" />
            <Route path="/call-analytics" redirect="/activity-analytics/calls" />
            <Route path="/sms-analytics" redirect="/activity-analytics/sms" />
            <Route path="/verification-analytics" page={VerificationAnalyticsPage} name="verificationAnalytics" />
            <Route path="/communications/calls" page={ActivitiesPage} name="communications" />
            <Route path="/communications/calls/{id:String}" page={ActivitiesPage} name="communicationsWithId" />
            <Route path="/communications/emails/{id:String}" page={ActivitiesPage} name="emailsWithId" />
            <Route path="/communications/emails" page={ActivitiesPage} name="emails" />
            <Route path="/calendar" page={CalendarPage} name="calendar" />
            <Route path="/tasks" page={TasksPage} name="tasks" />
            <Route path="/tasks/{id:String}" page={TasksPage} name="tasksWithId" />
            <Route path="/expirations" page={ExpirationsPage} name="expirations" />
            <Route path="/document-intelligence-analytics" page={DocumentIntelligenceAnalyticsPage} name="documentIntelligenceAnalytics" />
          </PrivateSet>
          <PrivateSet wrap={AdminLayout} unauthenticated="dashboard" roles={['ADMIN']} whileLoadingAuth={SkeletonLoader} whileLoadingPage={SkeletonLoader}>
            <Route path="/admin" page={AdminHomePage} name="adminHome" />
            <Route path="/admin/voe" redirect="/admin/voe/carriers" />
            <Route path="/admin/voe/contacts" page={AdminVoEContactsPage} name="adminVoeContacts" />
            <Route path="/admin/voe/suppressed" page={AdminVoESuppressedPage} name="adminVoeSuppressed" />
            <Route path="/admin/voe/carriers" page={AdminVoECarriersPage} name="adminVoeCarriers" />
            <Route path="/admin/voe/carriers/{carrierId:String}" page={AdminVoECarriersPage} name="adminVoeCarrier" />
            <Route path="/admin/integration" redirect="/admin/integration/api-clients" />
            <Route path="/admin/integration/api-clients" page={AdminIntegrationApiClientsPage} name="adminIntegrationApiClients" />
          </PrivateSet>
        </Set>
      )}

      <Route notfound page={NotFoundPage} />
    </Router>
  )
}

export default Routes
